'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { createOtpClient } from '@/lib/supabase/client'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)
  const [linkFailed, setLinkFailed] = useState(false)
  const router = useRouter()

  // `/auth/confirm` sends people here with ?link=expired when verifyOtp fails, so the bounce
  // can explain itself instead of looking like a dead end (NEA-263).
  //
  // ⚠️ Read in an EFFECT, never during render. This page is statically prerendered and cached
  // for a year; `window` doesn't exist server-side, and a render-time read would be exactly the
  // hydration mismatch NEA-245 was. The banner appears a frame after mount, which is fine —
  // it's an explanation, not a control. Using `useSearchParams` instead would drag the whole
  // page out of static generation.
  useEffect(() => {
    setLinkFailed(new URLSearchParams(window.location.search).get('link') === 'expired')
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setPending(true)
    setError('')

    const supabase = createOtpClient()
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim().toLowerCase(),
      options: { shouldCreateUser: false },
    })

    if (error) {
      // Don't reveal whether the email exists
      console.error('OTP error:', error.message)
    }

    // Always redirect — never reveal if the email exists. Carry any `next`
    // deep-link target through to verify so login returns there after the code.
    const next = new URLSearchParams(window.location.search).get('next')
    const verifyUrl = `/verify?email=${encodeURIComponent(email.trim().toLowerCase())}`
      + (next ? `&next=${encodeURIComponent(next)}` : '')
    router.push(verifyUrl)
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-full max-w-md bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <h1 className="font-display text-2xl font-bold text-gray-900 mb-1">ppleague</h1>
        <p className="text-gray-500 text-sm mb-6">Prediction League</p>

        {/* ⚠️ Covers a SPENT link and an EXPIRED one — verifyOtp fails identically for both and
            can't tell them apart, so this must not claim "already used". And it says "some
            workplace email security" rather than naming a culprit, because occasionally it
            really is a second click. The reassurance is the point: without it, being bounced
            here from your own invite reads as though the link was intercepted. */}
        {linkFailed && (
          <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-900">That sign-in link is no longer valid.</p>
            <p className="mt-1 text-xs text-amber-800">
              Links expire, and some workplace email security opens them automatically before you get
              there. Nothing is wrong with your account — enter your address below and we&rsquo;ll send
              you a fresh code.
            </p>
          </div>
        )}

        <p className="text-gray-700 mb-2">
          Enter your email address to receive a login code.
        </p>
        <p className="text-gray-500 text-sm mb-6">
          ppleague is invite-only — use the email address your invitation was sent to.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="you@example.com"
            />
          </div>

          {error && (
            <p className="text-red-600 text-sm">{error}</p>
          )}

          <button
            type="submit"
            disabled={pending}
            className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md transition-colors"
          >
            {pending ? 'Sending…' : 'Send code'}
          </button>
        </form>
      </div>
    </div>
  )
}
